/**
 * Hostname → role routing.
 *
 * A single React build powers four PWAs:
 *
 *     tsgo.store         → Customer app
 *     store.tsgo.store   → Store Owner console
 *     rider.tsgo.store   → Rider app
 *     admin.tsgo.store   → Super admin console
 *
 * This module reads the browser hostname once at boot and exposes:
 *
 *  - roleForHost():          the intended role for the current host
 *  - defaultRouteFor(role):  where to land after auth for that role
 *  - loginRouteFor(role):    the role-specific login portal URL
 *
 * All four hostnames serve the SAME bundle; the split lives in redirect
 * logic (see AuthContext / AppRoutes). Unknown hostnames default to the
 * customer app, so preview / localhost / *.emergentagent.com keep working.
 */

export type HostRole = "customer" | "store" | "rider" | "admin";

export function hostname(): string {
    if (typeof window === "undefined") return "";
    return (window.location.hostname || "").toLowerCase();
}

export function roleForHost(host: string = hostname()): HostRole {
    if (host.startsWith("store.")) return "store";
    if (host.startsWith("rider.")) return "rider";
    if (host.startsWith("admin.")) return "admin";
    return "customer";
}

export function defaultRouteFor(role: HostRole): string {
    switch (role) {
        case "store":
        case "admin":
            return "/admin/dashboard";
        case "rider":
            return "/rider/dashboard";
        default:
            return "/home";
    }
}

export function loginRouteFor(role: HostRole): string {
    switch (role) {
        case "store":
            return "/store/login";
        case "rider":
            return "/rider/login";
        case "admin":
            return "/superadmin/login";
        default:
            return "/login";
    }
}

/**
 * Route the anonymous user should land on based on hostname. Called from
 * top-level splash/welcome screens to skip the customer welcome screen on
 * non-customer subdomains.
 */
export function anonymousLandingRoute(): string {
    return loginRouteFor(roleForHost());
}
